export function getLineLengthMeters(coords) {
  let length = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1, z1 = 0] = coords[i];
    const [lon2, lat2, z2 = 0] = coords[i + 1];
    const meanLat = (lat1 + lat2) / 2;
    const dx = (lon2 - lon1) * 111320 * Math.cos(meanLat * Math.PI / 180);
    const dy = (lat2 - lat1) * 110540;
    const dz = (z2 - z1);
    length += Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  return length;
}

/**
 * Calculate adaptive step size based on total geometry length
 * Uses coarser steps for longer lines to avoid heavy processing
 * Provides extra safety to prevent intentional backend blocking with very long geometries
 * @param {number} totalLengthMeters - Total length of the geometry in meters
 * @returns {number} - Step size in meters
 */
export function getAdaptiveStepSize(totalLengthMeters) {
  if (totalLengthMeters < 20) return 5;
  else if (totalLengthMeters < 100) return 20;
  else if (totalLengthMeters < 500) return 40;
  else if (totalLengthMeters < 1000) return 60;
  else if (totalLengthMeters < 2000) return 80;
  else if (totalLengthMeters < 3000) return 100;
  else if (totalLengthMeters < 4000) return 120;
  else if (totalLengthMeters < 5000) return 140;
  else if (totalLengthMeters < 6000) return 160;
  else if (totalLengthMeters < 7000) return 180;
  else if (totalLengthMeters < 8000) return 200;
  else if (totalLengthMeters < 9000) return 220;
  else if (totalLengthMeters < 10000) return 240;
  else if (totalLengthMeters < 11000) return 260;
  else if (totalLengthMeters < 12000) return 280;
  else if (totalLengthMeters < 13000) return 300;
  else if (totalLengthMeters < 14000) return 320;
  else if (totalLengthMeters < 15000) return 340;
  else if (totalLengthMeters < 16000) return 360;
  else if (totalLengthMeters < 17000) return 380;
  else if (totalLengthMeters < 18000) return 400;
  else if (totalLengthMeters < 19000) return 420;
  else if (totalLengthMeters < 20000) return 440;
  else if (totalLengthMeters < 30000) return 700;
  else if (totalLengthMeters < 40000) return 900;
  else if (totalLengthMeters < 50000) return 1100;
  else return 1500;
}

export function interpolateLine3D(coords, stepSizeMeters = 10) {
  if (coords.length < 2) return coords;
  const result = [];
  const MAX_POINTS = 1000; // DDoS protection: hard limit on interpolation points
  
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const meanLat = (a[1] + b[1]) / 2;
    const dx = (b[0] - a[0]) * 111320 * Math.cos(meanLat * Math.PI / 180);
    const dy = (b[1] - a[1]) * 110540;
    const dz = (b[2] - a[2]);
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const steps = Math.max(1, Math.ceil(dist / stepSizeMeters));
    for (let j = 0; j < steps; j++) {
      const t = j / steps;
      result.push([
        a[0] + t * (b[0] - a[0]),
        a[1] + t * (b[1] - a[1]),
        a[2] + t * (b[2] - a[2])
      ]);
      // Stop if we've reached the maximum point limit
      if (result.length >= MAX_POINTS) {
        break;
      }
    }
    if (result.length >= MAX_POINTS) {
      break;
    }
  }
  
  // Always add the final point if we haven't reached it yet and haven't hit the limit
  if (result.length < MAX_POINTS && result.length > 0) {
    const lastCoord = coords[coords.length - 1];
    const lastResult = result[result.length - 1];
    // Only add if it's different from the last point in result
    if (lastCoord[0] !== lastResult[0] || lastCoord[1] !== lastResult[1] || lastCoord[2] !== lastResult[2]) {
      result.push(lastCoord);
    }
  }
  
  return result;
}